import React, { useEffect, useState } from 'react';
import { 
  Modal, Box, Typography, IconButton, TextField, Button, MenuItem, 
  Checkbox, FormControlLabel, Snackbar, Alert, FormControl, InputLabel, Select, 
  Paper, Divider, InputAdornment, Grid, Autocomplete
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CategoryIcon from '@mui/icons-material/Category';
import GroupsIcon from '@mui/icons-material/Groups';
import GroupIcon from '@mui/icons-material/Group';
import NotesIcon from '@mui/icons-material/Notes';
import PersonIcon from '@mui/icons-material/Person';
import PublicIcon from '@mui/icons-material/Public';
import PeopleIcon from '@mui/icons-material/People';
import BuildIcon from '@mui/icons-material/Build';
import { createGatheringAsAdmin, fetchUsers, fetchAllUsers } from '../../../../services/api';
import { fetchSpaces, fetchAreasBySpaceId, fetchGatheringsByDateAndSpace } from '../../../../services/api/endpoints/spaces';
import { fetchAreaSchedules, AreaSchedule } from '../../../../services/areaScheduleService';
import TimelineView from '../gathering-detail/TimelineView';
import RichTextEditor from '../../../../shared/components/RichTextEditor';
import { descriptionToEditorHtml } from '../../../../utils/richText';
import { Area, User, Space } from '../../../../types';
import { Interest } from '../../../../types';
import { GatheringCreateRequest } from '../../../../schemas/gatherings';
import dayjs from 'dayjs';

const FORM_STORAGE_KEY = "create-gathering-form";

interface BackendUser { 
  id: string; display_name?: string; first_name?: string; last_name?: string; email?: string; firebase_id?: string; 

}

interface GatheringCreateModalProps { 
  isOpen: boolean; 
  onClose: () => void; 
  token: string; 
  onCreated?: () => void; 
  onCreatedWithId?: (id: string) => void; 
}

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: '1200px',
  maxHeight: '90vh',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: { xs: 2, sm: 4 },
  borderRadius: 2,
  outline: 0,
  overflow: 'auto'
};

const GatheringCreateModal: React.FC<GatheringCreateModalProps> = ({
  isOpen,
  onClose,
  token,
  onCreated,
  onCreatedWithId
}) => {
  const [formData, setFormData] = useState<Partial<GatheringCreateRequest>>(() => {
    // initialize from localStorage
    const saved = localStorage.getItem(FORM_STORAGE_KEY);
    return saved ? JSON.parse(saved) : {};
  });
  const [ownerId, setOwnerId] = useState<string>(''); 
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState<BackendUser[]>([]);
  const [spaces, setSpaces] = useState<Space[]>([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">("success");
  const [areas, setAreas] = useState<Area[]>([]);
  // Scheduling state
  const [selectedDate, setSelectedDate] = useState<string>(() => {
    const saved = localStorage.getItem(FORM_STORAGE_KEY);
    if (saved) {
      try { const parsed = JSON.parse(saved); return parsed.__schedule?.date || ''; } catch {}
    }
    return '';
  });
  const [selectedDuration, setSelectedDuration] = useState<number>(() => {
    const saved = localStorage.getItem(FORM_STORAGE_KEY);
    if (saved) {
      try { const parsed = JSON.parse(saved); return parsed.__schedule?.duration || 60; } catch {}
    }
    return 60;
  });
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<{ startTime: string; endTime: string; areaId: string } | null>(() => {
    const saved = localStorage.getItem(FORM_STORAGE_KEY);
    if (saved) {
      try { const parsed = JSON.parse(saved); return parsed.__schedule?.slot || null; } catch {}
    }
    return null;
  });
  const [areaSchedules, setAreaSchedules] = useState<AreaSchedule[]>([]);
  const [isLoadingSchedules, setIsLoadingSchedules] = useState<boolean>(false);
  const [scheduleError, setScheduleError] = useState<string | null>(null);
  const [scheduleDateError, setScheduleDateError] = useState<string | null>(null);
  // Touched map for fields and submitted flag for validation UX
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitted, setSubmitted] = useState<boolean>(false);

  useEffect(() => {
    // persist form + schedule into one object
    const persisted: any = { ...formData, __schedule: { date: selectedDate, duration: selectedDuration, slot: selectedTimeSlot } };
    localStorage.setItem(FORM_STORAGE_KEY, JSON.stringify(persisted));
  }, [formData, selectedDate, selectedDuration, selectedTimeSlot]);

  // Load users and spaces when modal opens
  useEffect(() => {
    if (!isOpen) return;
    fetchAllUsers(token)
      .then((res) => setUsers(res as unknown as BackendUser[]))
      .catch(console.error);
    fetchSpaces(token)
      .then(setSpaces)
      .catch(console.error);
  }, [isOpen, token]);

  useEffect(() => {
    if (formData.space_id) {
      // load areas for the selected space
      fetchAreasBySpaceId(token, formData.space_id)
        .then(setAreas)
        .catch(console.error);
    } else {
      setAreas([]);
    }
  }, [formData.space_id, token]);

  // Load schedules when space and date are set
  useEffect(() => {
    const load = async () => {
      if (!isOpen || !formData.space_id || !selectedDate) return;
      setIsLoadingSchedules(true);
      setScheduleError(null);
      try {
        const schedules = await fetchAreaSchedules(
          fetchAreasBySpaceId,
          fetchGatheringsByDateAndSpace,
          token,
          selectedDate,
          formData.space_id,
          spaces.find(s => s.id === formData.space_id)?.name
        );
        setAreaSchedules(schedules);
      } catch (e) {
        console.error('Error loading schedules', e);
        setScheduleError('Failed to load schedules.');
      } finally {
        setIsLoadingSchedules(false);
      }
    };
    load();
  }, [isOpen, formData.space_id, selectedDate, token, spaces]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    // If space changes, clear only the selected time slot (keep date and duration)
    if (name === 'space_id') {
      setSelectedTimeSlot(null);
    }
    setFormData({ ...formData, [name]: value });
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
  };

  const handleDateInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedDate(e.target.value);
    setSelectedTimeSlot(null);
    // Past-date validation (today is allowed) using dayjs start-of-day to avoid timezone issues
    if (e.target.value) {
      const todayStart = dayjs().startOf('day');
      const selectedStart = dayjs(e.target.value).startOf('day');
      if (selectedStart.isBefore(todayStart)) {
        setScheduleDateError('Cannot schedule gatherings in the past. Please select a future date.');
      } else {
        setScheduleDateError(null);
      }
    } else {
      setScheduleDateError(null);
    }
  };
  const handleDurationSelect = (e: React.ChangeEvent<{ value: unknown }>) => {
    setSelectedDuration(Number(e.target.value));
  };
  const handleTimeSlotSelect = (startTime: string, endTime: string, areaId: string) => {
    setSelectedTimeSlot({ startTime, endTime, areaId });
  };

  // Close handler: clear persisted form and close modal
  const handleClose = () => {
    localStorage.removeItem(FORM_STORAGE_KEY);
    onClose();
  };

  // Checkbox handler for boolean fields
  const handleCheckbox = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    // Enforce mutual exclusivity between is_public and is_members_only
    if (name === 'is_public' && checked) {
      setFormData({ ...formData, is_public: true, is_members_only: false });
      return;
    }
    if (name === 'is_members_only' && checked) {
      setFormData({ ...formData, is_members_only: true, is_public: false });
      return;
    }
    setFormData({ ...formData, [name]: checked });
  };

  // Validation helpers
  const communityError = !formData.community || formData.community.trim().length < 3 ? 'Community is required (min 3 chars)' : '';
  const titleError = !formData.title || formData.title.trim() === '' ? 'Title is required' : '';
  const ownerError = !ownerId ? 'Owner is required' : '';
  const spaceError = !formData.space_id ? 'Space is required' : '';
  const scheduleErrorMsg = !selectedDate || !selectedTimeSlot ? 'Select a date and a time slot' : '';
  const isCreateDisabled = !!(titleError || communityError || ownerError || spaceError || scheduleErrorMsg || scheduleDateError) || saving;

  const handleSave = async () => {
    setSubmitted(true);
    if (isCreateDisabled || !selectedTimeSlot) return;

    const payload: GatheringCreateRequest = {
      title: formData.title!.trim(),
      description: formData.description ?? '',
      date: selectedDate,
      start_time: selectedTimeSlot.startTime,
      end_time: selectedTimeSlot.endTime,
      duration: selectedDuration,
      category: (formData.category as string) ?? 'Other',
      event_size: formData.event_size ? Number(formData.event_size) : 0,
      additional_notes: formData.additional_notes ?? '',
      is_public: !!formData.is_public,
      is_members_only: !!formData.is_members_only,
      is_fabrik_hosted: !!formData.is_fabrik_hosted,
      is_builder_gathering: !!formData.is_builder_gathering,
      community: formData.community!.trim(),
      space_id: formData.space_id!,
      area_ids: [selectedTimeSlot.areaId],
    };

    try {
      setSaving(true);
      const created = await createGatheringAsAdmin(token, ownerId, payload);
      setSnackbarMessage('Gathering created successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      onCreated?.();
      onCreatedWithId?.(created.id);
      handleClose();
    } catch (err) {
      console.error('Error creating gathering:', err);
      setSnackbarMessage('Failed to create gathering.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setSaving(false);
    }
  };


  return (
    <>
 
    <Modal open={isOpen} onClose={handleClose}>
      <Box sx={modalStyle}>
        <IconButton onClick={handleClose} sx={{ position: 'absolute', top: 16, right: 16 }}>
          <CloseIcon />
        </IconButton>

        <Typography variant="h6" gutterBottom>
          Create Gathering
        </Typography>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 12 }}>
            {/* Event Details Section */}
            <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CategoryIcon color="primary" />
                <Typography variant="h6" fontWeight="medium">Event Details</Typography>
              </Box>
              <Divider sx={{ my: 1 }} />

              <TextField
                label="Title"
                name="title"
                value={formData.title || ''}
                onChange={handleChange}
                onBlur={handleBlur}
                fullWidth
                required
                variant="standard"
                InputProps={{
                  style: { fontSize: '1.75rem', fontWeight: 'bold' }
                }}
                error={(touched.title || submitted) && !!titleError}
                helperText={(touched.title || submitted) ? titleError : ' '}
                sx={{ mb: 2 }}
              />

              {/* Row 1: Community, Event Size, Category, Owner */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid size={{ xs: 12, md: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary">Community</Typography>
                  <TextField
                    name="community"
                    value={formData.community || ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    fullWidth
                    required
                    variant="outlined"
                    size="small"
                    error={(touched.community || submitted) && !!communityError}
                    helperText={(touched.community || submitted) ? communityError : ' '}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <GroupsIcon fontSize="small" />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary">Event Size</Typography>
                  <TextField
                    name="event_size"
                    type="number"
                    value={formData.event_size || ''}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <GroupIcon fontSize="small" />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary">Category</Typography>
                  <TextField
                    select
                    name="category"
                    value={formData.category || ""}
                    onChange={handleChange}
                    fullWidth
                    required
                    variant="outlined"
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CategoryIcon fontSize="small" />
                        </InputAdornment>
                      )
                    }}
                  >
                    {Object.values(Interest).map((interest) => (
                      <MenuItem key={interest} value={interest}>
                        {interest}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary">Owner</Typography>
                  <Autocomplete
                    value={users.find(u => u.id === ownerId) || null}
                    onChange={(event, newValue) => {
                      setOwnerId(newValue?.id || '');
                    }}
                    onBlur={() => setTouched(prev => ({ ...prev, ownerId: true }))}
                    options={users}
                    getOptionLabel={(option) => 
                      `${option.first_name ?? ''} ${option.last_name ?? ''}`.trim() + (option.email ? ` (${option.email})` : '')
                    }
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        size="small"
                        required
                        error={(touched.ownerId || submitted) && !!ownerError}
                        helperText={(touched.ownerId || submitted) ? ownerError : ' '}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start">
                                <PersonIcon fontSize="small" />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    fullWidth
                  />
                </Grid>
              </Grid>

              {/* Row 2: Description and Additional Notes */}
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">Description</Typography>
                  <RichTextEditor
                    content={descriptionToEditorHtml(formData.description)}
                    onChange={(html) => setFormData((prev) => ({ ...prev, description: html }))}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">Additional Notes</Typography>
                  <TextField
                    name="additional_notes"
                    value={formData.additional_notes || ''}
                    onChange={handleChange}
                    fullWidth
                    multiline
                    rows={3}
                    variant="outlined"
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                          <NotesIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <FormControlLabel
                  control={<Checkbox checked={!!formData.is_public} onChange={handleCheckbox} name="is_public" />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <PublicIcon fontSize="small" sx={{ mr: 0.5 }} />
                      <Typography variant="body2">Public</Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  control={<Checkbox checked={!!formData.is_members_only} onChange={handleCheckbox} name="is_members_only" />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <PeopleIcon fontSize="small" sx={{ mr: 0.5 }} />
                      <Typography variant="body2">Members Only</Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  control={<Checkbox checked={!!formData.is_fabrik_hosted} onChange={handleCheckbox} name="is_fabrik_hosted" />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body2">Fabrik Hosted</Typography>
                    </Box>
                  }
                />
                <Box sx={{ flexBasis: '100%' }}>
                  <FormControlLabel
                    control={<Checkbox checked={!!formData.is_builder_gathering} onChange={handleCheckbox} name="is_builder_gathering" />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <BuildIcon fontSize="small" sx={{ mr: 0.5 }} />
                        <Typography variant="body2">Builder Gathering</Typography>
                      </Box>
                    }
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
                    Attendees get the builder follow-up emails instead of the generic sequence. Saved now, sent to HubSpot when this gathering is published.
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, md: 12 }}>
            {/* Scheduling Section */}
            <Paper elevation={0} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CategoryIcon color="primary" />
                <Typography variant="h6" fontWeight="medium">Schedule</Typography>
              </Box>
              <Divider sx={{ my: 1 }} />

              {/* Date, Space, Duration (same row) */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Typography variant="subtitle2" color="text.secondary">Date</Typography>
                  <TextField
                    type="date"
                    value={selectedDate}
                    onChange={handleDateInputChange}
                    fullWidth
                    variant="outlined"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Typography variant="subtitle2" color="text.secondary">Space</Typography>
                  <TextField
                    select
                    name="space_id"
                    value={formData.space_id || ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    fullWidth
                    required
                    variant="outlined"
                    size="small"
                    error={(touched.space_id || submitted) && !!spaceError}
                    helperText={(touched.space_id || submitted) ? spaceError : ' '}
                  >
                    {spaces.map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        {s.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Typography variant="subtitle2" color="text.secondary">Duration</Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      native
                      value={String(selectedDuration)}
                      onChange={(e) => setSelectedDuration(Number((e.target as HTMLSelectElement).value))}
                    >
                      {Array.from({ length: 24 }, (_, i) => (i + 1) * 30).map(opt => (
                        <option key={opt} value={opt}>{`${Math.floor(opt/60) > 0 ? `${Math.floor(opt/60)}h ` : ''}${opt % 60 ? `${opt%60}m` : ''}`}</option>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {scheduleDateError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {scheduleDateError}
                </Alert>
              )}

              <Box>
                {(selectedDate && formData.space_id) ? (
                  <TimelineView
                    date={selectedDate}
                    areas={areaSchedules}
                    onTimeSlotSelect={handleTimeSlotSelect}
                    selectedSlot={selectedTimeSlot}
                    gatheringDuration={selectedDuration}
                    isLoading={isLoadingSchedules}
                    error={scheduleError}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Select a date and a space to schedule this gathering.
                  </Typography>
                )}
                {(!selectedDate || !selectedTimeSlot) && submitted ? (
                  <Typography variant="caption" color="error">Select a date and an available time slot</Typography>
                ) : null}
              </Box>
            </Paper>
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={isCreateDisabled}>
            {saving ? 'Creating…' : 'Create Gathering'}
          </Button>
        </Box>

      </Box>

      
 
    </Modal>

    <Snackbar
      open={snackbarOpen}
      autoHideDuration={4000}
      onClose={() => setSnackbarOpen(false)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert
        onClose={() => setSnackbarOpen(false)}
        severity={snackbarSeverity}
        variant="filled"
        sx={{ width: '100%' }}
      >
        {snackbarMessage}
      </Alert>
    </Snackbar>
    </>
  );
}; 
 
export default GatheringCreateModal;
